import React, { useEffect, useState, useContext, Suspense } from "react";
import {
  BrowserRouter,
  Switch,
  Route as RouterRoute,
  Redirect,
} from "react-router-dom";
import { ToastContainer, cssTransition } from "react-toastify";
import { LiveLocationDialogHost } from "../components/RouteFlow/LiveLocation";
import moment from "moment";

import LoggedInLayout from "../layout";
import Signup from "../pages/Signup";
import Login from "../pages/Login/";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";
import MobilePushManager from "../components/MobilePushManager";
import BackdropLoading from "../components/BackdropLoading";

// Carregamento sob demanda (code splitting): antes TODAS as paginas do CRM
// (mesmo as raramente usadas, tipo Campanhas ou Relatorio de Vendas) iam
// juntas num arquivo so de ~2,2MB — e esse arquivo inteiro tinha que ser
// baixado/processado ate pra abrir uma pagina isolada como /ficha-embed
// (a ficha completa, usada dentro de um iframe no app Visita) ou /checkout,
// que não usam quase nada do resto. Agora cada pagina vira um pedaço
// separado, baixado só quando alguém realmente navega pra ela.
const Dashboard = React.lazy(() => import("../pages/Dashboard/"));
const TicketResponsiveContainer = React.lazy(() => import("../pages/TicketResponsiveContainer"));
const Connections = React.lazy(() => import("../pages/Connections/"));
const Settings = React.lazy(() => import("../pages/Settings/"));
const Financeiro = React.lazy(() => import("../pages/Financeiro/"));
const Users = React.lazy(() => import("../pages/Users"));
const Contacts = React.lazy(() => import("../pages/Contacts/"));
const Couriers = React.lazy(() => import("../pages/Couriers/"));
const ContactImportPage = React.lazy(() => import("../pages/Contacts/import"));
const ChatMoments = React.lazy(() => import("../pages/Moments"));
const Queues = React.lazy(() => import("../pages/Queues/"));
const MessagesAPI = React.lazy(() => import("../pages/MessagesAPI/"));
const Helps = React.lazy(() => import("../pages/Helps/"));
const ContactLists = React.lazy(() => import("../pages/ContactLists/"));
const ContactListItems = React.lazy(() => import("../pages/ContactListItems/"));
const Companies = React.lazy(() => import("../pages/Companies/"));
const Wallets = React.lazy(() => import("../pages/Wallets/"));
const QuickMessages = React.lazy(() => import("../pages/QuickMessages/"));
const Schedules = React.lazy(() => import("../pages/Schedules"));
const Campaigns = React.lazy(() => import("../pages/Campaigns"));
const CampaignsConfig = React.lazy(() => import("../pages/CampaignsConfig"));
const CampaignReport = React.lazy(() => import("../pages/CampaignReport"));
const Annoucements = React.lazy(() => import("../pages/Annoucements"));
const Chat = React.lazy(() => import("../pages/Chat"));
const Prompts = React.lazy(() => import("../pages/Prompts"));
const AllConnections = React.lazy(() => import("../pages/AllConnections/"));
const Reports = React.lazy(() => import("../pages/Reports"));
const RelatorioVendas = React.lazy(() => import("../pages/RelatorioVendas"));
const QueueIntegration = React.lazy(() => import("../pages/QueueIntegration"));
const Files = React.lazy(() => import("../pages/Files/"));
const ToDoList = React.lazy(() => import("../pages/ToDoList/"));
const BirthdaySettingsPage = React.lazy(() => import("../pages/BirthdaySettings"));
const CallHistoricals = React.lazy(() => import("../pages/CallHistoricals"));
// FlowBuilderConfig é exportado com nome (não "export default"), por isso o
// .then(...) — React.lazy só sabe lidar com { default: Componente }.
const FlowBuilderConfig = React.lazy(() =>
  import("../pages/FlowBuilderConfig").then((m) => ({ default: m.FlowBuilderConfig }))
);
const FlowBuilder = React.lazy(() => import("../pages/FlowBuilder"));
const CampaignsPhrase = React.lazy(() => import("../pages/CampaignsPhrase"));
const Floup = React.lazy(() => import("../pages/Floup"));
const FloupDashboard = React.lazy(() => import("../pages/FloupDashboard"));
const ContactRegistrations = React.lazy(() => import("../pages/ContactRegistrations"));
const Leads = React.lazy(() => import("../pages/Leads"));
const Leads2 = React.lazy(() => import("../pages/Leads2"));
const LeadsRafaelle = React.lazy(() => import("../pages/LeadsRafaelle"));
const VisitCardsPage = React.lazy(() => import("../pages/VisitCardsPage"));
const AppClientsPage = React.lazy(() => import("../pages/AppClientsPage"));
const RegistrationBillingConfig = React.lazy(() => import("../pages/RegistrationBillingConfig"));
const RegistrationsCount = React.lazy(() => import("../pages/RegistrationsCount"));
const RecentActivities = React.lazy(() => import("../pages/RecentActivities"));
const CustomersMap = React.lazy(() => import("../pages/CustomersMap"));
const PublicCheckout = React.lazy(() => import("../pages/PublicCheckout"));
const PublicRoute = React.lazy(() => import("../pages/PublicRoute"));
const PublicFicha = React.lazy(() => import("../pages/PublicFicha"));
const FichaEmbedPage = React.lazy(() => import("../pages/FichaEmbedPage"));
const ParticularPixCheckout = React.lazy(() => import("../pages/ParticularPixCheckout"));
const ReceiptSearch = React.lazy(() => import("../pages/ReceiptSearch"));
const LoanCadastroFlow = React.lazy(() => import("../pages/LoanCadastroFlow"));
const MotoboyManager = React.lazy(() => import("../pages/MotoboyManager"));
const DigitalAccountSettings = React.lazy(() => import("../pages/DigitalAccountSettings"));

const CHECKOUT_ROOT_HOSTS = new Set(["paguesuadata.com"]);
const PARTICULAR_PIX_ROOT_HOSTS = new Set(
  String(process.env.REACT_APP_PARTICULAR_PIX_ROOT_HOSTS || "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
);
const LOAN_CADASTRO_ROOT_HOSTS = new Set([
  "emprestimopessoall.com",
  "www.emprestimopessoall.com",
  ...String(process.env.REACT_APP_LOAN_CADASTRO_ROOT_HOSTS || "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean),
]);

const matchesCurrentHostname = (allowedHosts) => {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = String(window.location.hostname || "").toLowerCase().trim();
  return allowedHosts.has(hostname);
};

const shouldRenderCheckoutAtRoot = () => {
  return matchesCurrentHostname(CHECKOUT_ROOT_HOSTS);
};

const shouldRenderParticularPixAtRoot = () => {
  return matchesCurrentHostname(PARTICULAR_PIX_ROOT_HOSTS);
};

const shouldRenderLoanCadastroAtRoot = () => {
  return matchesCurrentHostname(LOAN_CADASTRO_ROOT_HOSTS);
};

const ToastDropTransition = cssTransition({
  enter: "agz-toast-enter",
  exit: "agz-toast-exit",
  duration: [280, 180],
  appendPosition: false,
  collapse: true
});

const RoutesContent = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  // Verificar se a empresa está vencida
  const isCompanyExpired = () => {
    if (!user || !user.company || user.company.id === 1) {
      return false; // Empresa ID 1 nunca expira
    }

    const dueDate = user.company.dueDate;
    if (!dueDate) return false;

    // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
    const hojeInicio = moment().startOf('day');
    const vencimentoInicio = moment(dueDate).startOf('day');

    // Empresa está vencida apenas após o dia do vencimento
    return hojeInicio.isAfter(vencimentoInicio, 'day');
  };

  return (
    <TicketsContextProvider>
      <MobilePushManager />
      <Suspense fallback={<BackdropLoading />}>
      <Switch>
        <Route exact path="/login" component={Login} />
        <Route exact path="/signup" component={Signup} />
        <WhatsAppsProvider>
          <LoggedInLayout hideMenu={isCompanyExpired()}>
            <Route
              exact
              path="/financeiro"
              component={Financeiro}
              isPrivate
            />

            <Route
              exact
              path="/financeiro-aberto"
              component={Financeiro}
              isPrivate
            />

            <Route
              exact
              path="/companies"
              component={Companies}
              isPrivate
            />
            <Route
              exact
              path="/birthday-settings"
              component={BirthdaySettingsPage}
              isPrivate
            />
            <Route exact path="/" component={(props) => {
              try {
                if (localStorage.getItem("agilzap_home") === "/cadastro") {
                  return <Redirect to="/cadastro" />;
                }
              } catch (e) {}
              return <Dashboard {...props} />;
            }} isPrivate />
            <Route exact path="/call-historicals" component={CallHistoricals} isPrivate />
            <Route
              exact
              path="/tickets/:ticketId?"
              component={TicketResponsiveContainer}
              isPrivate
            />
            <Route
              exact
              path="/connections"
              component={Connections}
              isPrivate
            />
            <Route
              exact
              path="/quick-messages"
              component={QuickMessages}
              isPrivate
            />
            <Route exact path="/todolist" component={ToDoList} isPrivate />
            <Route
              exact
              path="/schedules"
              component={Schedules}
              isPrivate
            />
            <Route exact path="/contacts" component={Contacts} isPrivate />
            <Route
              exact
              path="/registrations"
              component={ContactRegistrations}
              isPrivate
            />
            <Route
              exact
              path="/cadastro"
              component={ContactRegistrations}
              alwaysRender
            />
            <Route
              exact
              path="/leads"
              component={Leads}
              isPrivate
            />
            <Route
              exact
              path="/leads2"
              component={Leads2}
              isPrivate
            />
            <Route
              exact
              path="/leads-marcella"
              component={Leads2}
              isPrivate
            />
            <Route
              exact
              path="/leads-rafaelle"
              component={LeadsRafaelle}
              isPrivate
            />
            <Route
              exact
              path="/visitas"
              component={VisitCardsPage}
              isPrivate
            />
            <Route
              exact
              path="/clientes-app"
              component={AppClientsPage}
              isPrivate
            />
            <Route
              exact
              path="/motoboys"
              component={MotoboyManager}
              isPrivate
            />
            <Route
              exact
              path="/digital-account"
              component={DigitalAccountSettings}
              isPrivate
            />
            <Route
              exact
              path="/registrations-billing"
              component={RegistrationBillingConfig}
              isPrivate
            />
            <Route
              exact
              path="/registrations-count"
              component={RegistrationsCount}
              isPrivate
            />
            <Route
              exact
              path="/recent-activities"
              component={RecentActivities}
              isPrivate
            />
            <Route
              exact
              path="/customers-map"
              component={CustomersMap}
              isPrivate
            />
            <Route
              exact
              path="/receipt-search"
              component={ReceiptSearch}
              isPrivate
            />
            <Route exact path="/couriers" component={Couriers} isPrivate />
            <Route
              exact
              path="/contacts/import"
              component={ContactImportPage}
              isPrivate
            />
            <Route exact path="/wallets" component={Wallets} isPrivate />
            <Route exact path="/helps" component={Helps} isPrivate />
            <Route exact path="/users" component={Users} isPrivate />
            <Route
              exact
              path="/messages-api"
              component={MessagesAPI}
              isPrivate
            />
            <Route
              exact
              path="/settings"
              component={Settings}
              isPrivate
            />
            <Route exact path="/queues" component={Queues} isPrivate />
            <Route exact path="/reports" component={Reports} isPrivate />
            <Route
              exact
              path="/relatorio-vendas"
              component={RelatorioVendas}
              isPrivate
            />
            <Route
              exact
              path="/queue-integration"
              component={QueueIntegration}
              isPrivate
            />
            <Route
              exact
              path="/announcements"
              component={Annoucements}
              isPrivate
            />
            <Route exact path="/chats/:id?" component={Chat} isPrivate />
            <Route exact path="/files" component={Files} isPrivate />
            <Route
              exact
              path="/moments"
              component={ChatMoments}
              isPrivate
            />
            <RouterRoute
              exact
              path={["/Kanban", "/kanban"]}
              render={() => <Redirect to="/tickets" />}
            />
            <RouterRoute
              exact
              path={["/TagsKanban", "/tagsKanban"]}
              render={() => <Redirect to="/tickets" />}
            />
            <RouterRoute
              exact
              path="/tags"
              render={() => <Redirect to="/contacts" />}
            />
            <Route exact path="/prompts" component={Prompts} isPrivate />
            <Route
              exact
              path="/allConnections"
              component={AllConnections}
              isPrivate
            />

            <Route
              exact
              path="/plugins/floup"
              component={Floup}
              isPrivate
            />
            <Route
              exact
              path="/plugins/floup/dashboard"
              component={FloupDashboard}
              isPrivate
            />

            <Route
              exact
              path="/phrase-lists"
              component={CampaignsPhrase}
              isPrivate
            />
            <Route
              exact
              path="/flowbuilders"
              component={FlowBuilder}
              isPrivate
            />
            <Route
              exact
              path="/flowbuilder/:id?"
              component={FlowBuilderConfig}
              isPrivate
            />

            {showCampaigns && (
              <>
                <Route
                  exact
                  path="/contact-lists"
                  component={ContactLists}
                  isPrivate
                />
                <Route
                  exact
                  path="/contact-lists/:contactListId/contacts"
                  component={ContactListItems}
                  isPrivate
                />
                <Route
                  exact
                  path="/campaigns"
                  component={Campaigns}
                  isPrivate
                />
                <Route
                  exact
                  path="/campaign/:campaignId/report"
                  component={CampaignReport}
                  isPrivate
                />
                <Route
                  exact
                  path="/campaigns-config"
                  component={CampaignsConfig}
                  isPrivate
                />
              </>
            )}
          </LoggedInLayout>
        </WhatsAppsProvider>
      </Switch>
      </Suspense>
      <LiveLocationDialogHost />
      <ToastContainer
        position="top-center"
        autoClose={3200}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        limit={4}
        transition={ToastDropTransition}
        className="agz-toast-container"
        toastClassName="agz-toast"
        bodyClassName="agz-toast-body"
        progressClassName="agz-toast-progress"
      />
    </TicketsContextProvider>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<BackdropLoading />}>
        <Switch>
          <RouterRoute
            exact
            path="/"
            render={(props) =>
              shouldRenderCheckoutAtRoot() ? (
                <PublicCheckout {...props} />
              ) : shouldRenderParticularPixAtRoot() ? (
                <ParticularPixCheckout {...props} />
              ) : shouldRenderLoanCadastroAtRoot() ? (
                <LoanCadastroFlow {...props} />
              ) : (
                <RoutesContent />
              )
            }
          />
          <RouterRoute
            exact
            path="/interior"
            render={(props) =>
              shouldRenderLoanCadastroAtRoot() ? (
                <LoanCadastroFlow {...props} />
              ) : (
                <RoutesContent />
              )
            }
          />
          <RouterRoute
            exact
            path="/capital"
            render={(props) =>
              shouldRenderLoanCadastroAtRoot() ? (
                <LoanCadastroFlow {...props} />
              ) : (
                <RoutesContent />
              )
            }
          />
          <RouterRoute exact path="/checkout" component={PublicCheckout} />
          <RouterRoute exact path="/rota/:slug" component={PublicRoute} />
          <RouterRoute exact path="/ficha/:registrationId/:token" component={PublicFicha} />
          <RouterRoute exact path="/ficha-embed" component={FichaEmbedPage} />
          <RouterRoute exact path="/pix-particular" component={ParticularPixCheckout} />
          <RouterRoute path="/" component={RoutesContent} />
        </Switch>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
